import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { issueKeys, type IssueFilters } from "@/lib/query-keys";
import type {
  IssueRecord,
  IssueSummary,
  PaginatedResponse,
  ProjectIssue,
} from "@/types/api";

const PAGE_SIZE = 20;

/**
 * Serialise filters into the query string the issues endpoints accept.
 *
 * List filters are comma-separated for the multi-value ones, matching the
 * backend's `BaseInFilter` shape. `overdue: false` is sent, not skipped — it
 * means "only issues that are not late", which is a filter in its own right.
 */
export function buildIssueParams(filters: IssueFilters): URLSearchParams {
  const params = new URLSearchParams();
  const csv = (values: string[] | undefined) =>
    values && values.length > 0 ? values.join(",") : undefined;

  const status = csv(filters.status);
  if (status) params.set("status", status);
  const assignedTo = csv(filters.assigned_to);
  if (assignedTo) params.set("assigned_to", assignedTo);
  const createdBy = csv(filters.created_by);
  if (createdBy) params.set("created_by", createdBy);
  const plan = csv(filters.plan);
  if (plan) params.set("plan", plan);
  if (filters.record) params.set("record", filters.record);
  if (filters.due_before) params.set("due_before", filters.due_before);
  if (filters.due_after) params.set("due_after", filters.due_after);
  if (filters.overdue != null) params.set("overdue", String(filters.overdue));
  if (filters.search) params.set("search", filters.search);
  if (filters.ordering) params.set("ordering", filters.ordering);

  return params;
}

/**
 * The issues list for a project.
 *
 * Deliberately NOT offline-durable: unlike records, issues are not cached for
 * a jobsite with no signal in this wave, so this hook keeps the global
 * `gcTime` and the page shows an explicit offline message rather than an
 * error. See [[issues.spec]] § Out of scope.
 */
export function useProjectIssues(projectId: string, filters: IssueFilters = {}) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useInfiniteQuery({
    queryKey: issueKeys.list(projectId, filters),
    queryFn: async ({ pageParam = 1 }) => {
      const params = buildIssueParams(filters);
      params.set("page", String(pageParam));
      params.set("page_size", String(PAGE_SIZE));
      return fetchWithAuth<PaginatedResponse<ProjectIssue>>(
        `/projects/2.0/projects/${projectId}/issues/?${params.toString()}`,
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    select: (data) => ({
      ...data,
      // A page without `results` (an unexpected body) must not flatten to
      // `[undefined]` — every consumer reads fields off each issue. FE-DEBT-028.
      issues: data.pages.flatMap((page) => page.results ?? []),
      totalCount: data.pages[0]?.count ?? 0,
    }),
    placeholderData: keepPreviousData,
  });
}

/**
 * One issue, with its full activity trail.
 *
 * The detail endpoint is the only read that carries `updates`; the list shape
 * omits them on purpose.
 */
export function useProjectIssue(projectId: string, issueId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useQuery({
    queryKey: issueKeys.detail(projectId, issueId),
    queryFn: () =>
      fetchWithAuth<ProjectIssue>(
        `/projects/2.0/projects/${projectId}/issues/${issueId}/`,
      ),
    enabled: Boolean(projectId && issueId),
  });
}

/**
 * What an issue shows of its record: the photos, the pin and the plan it sits
 * on, read through the issue.
 *
 * Not the records endpoint. That needs record permissions, which a contributor
 * doesn't hold, and carries remarks, tags and metadata the issue never shows.
 * This one is open to anyone who can see the issue, so the evidence strip, the
 * location and the update camera's ghost work for everyone. See [[issues.spec]]
 * § The record behind an issue.
 *
 * Keyed by issue rather than by record: the issue is what grants the read, and
 * nesting under the project's issue key means every issue write refreshes it.
 */
export function useIssueRecord(projectId: string, issueId: string | undefined) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useQuery({
    queryKey: issueKeys.record(projectId, issueId ?? ""),
    queryFn: () =>
      fetchWithAuth<IssueRecord>(
        `/projects/2.0/projects/${projectId}/issues/${issueId}/record/`,
      ),
    enabled: Boolean(projectId && issueId),
  });
}

/**
 * Per-status counts for the list header.
 *
 * The `status` filter is stripped before the request AND before the query key
 * is built. The server ignores it too, but sending it would make every status
 * selection a separate cache entry holding identical numbers — one request per
 * tab for data that never varies by tab.
 */
export function useProjectIssueSummary(
  projectId: string,
  filters: IssueFilters = {},
) {
  const fetchWithAuth = useAuthenticatedFetch();
  const withoutStatus: IssueFilters = { ...filters };
  delete withoutStatus.status;
  delete withoutStatus.ordering;

  return useQuery({
    queryKey: issueKeys.summary(projectId, withoutStatus),
    queryFn: () => {
      const params = buildIssueParams(withoutStatus);
      const query = params.toString();
      return fetchWithAuth<IssueSummary>(
        `/projects/2.0/projects/${projectId}/issues/summary/${query ? `?${query}` : ""}`,
      );
    },
    enabled: Boolean(projectId),
    placeholderData: keepPreviousData,
  });
}
